import {
  alcoholOverdoseActions,
  alcoholOverdoseSigns,
  recoveryEvidenceLabels,
  recoveryFoods,
  recoveryMyths,
  recoveryPrinciples,
  recoverySourceById,
  recoverySources,
  recoverySupplements,
  recoveryTimeline,
  type RecoveryCard,
} from "@hanjan-shieo/content";
import { Link } from "./platform";
import "./recoveryGuide.css";

function EvidenceBadge({ card }: { card: RecoveryCard }) {
  const evidence = recoveryEvidenceLabels[card.evidenceLevel];
  return <span className={`recovery-evidence recovery-evidence--${card.evidenceLevel}`} title={evidence.description}>{evidence.label}</span>;
}

function SourceLinks({ ids }: { ids: readonly string[] }) {
  return <span className="recovery-card__sources">근거 {ids.map((id, index) => {
    const source = recoverySourceById(id);
    return source ? <span key={id}>{index > 0 && " · "}<a href={source.url} target="_blank" rel="noreferrer">{source.organization}</a></span> : null;
  })}</span>;
}

function RecoveryCards({ cards, kind }: { cards: RecoveryCard[]; kind: "food" | "supplement" }) {
  return <div className={`recovery-card-grid recovery-card-grid--${kind}`}>{cards.map((card, index) => <article className="recovery-card" key={card.id}>
    <div className="recovery-card__top"><span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><EvidenceBadge card={card} /></div>
    <h3>{card.title}</h3>
    <strong>{card.summary}</strong>
    <p>{card.detail}</p>
    <SourceLinks ids={card.sourceIds} />
  </article>)}</div>;
}

export function RecoveryGuide() {
  return <article className="recovery-guide">
    <header className="recovery-hero">
      <div className="recovery-hero__copy">
        <span className="eyebrow">과장 없는 숙취·회복 안내</span>
        <h1>술을 깨는 건 <em>시간.</em> 버티는 건 선택.</h1>
        <p>커피나 영양제가 알코올 처리 속도를 높여 주지는 않습니다. 지금 멈추기, 안전하게 귀가하기, 물·평소 식사·휴식으로 덜 힘들게 회복하는 방법을 근거 수준과 함께 확인하세요.</p>
        <nav className="recovery-jump" aria-label="회복 가이드 바로가기">
          <a href="#recover-now">지금 할 일</a>
          <a href="#recover-food">음식</a>
          <a href="#recover-supplements">영양제·약</a>
          <a href="#recover-emergency">119 신호</a>
        </nav>
      </div>
      <div className="recovery-clock" aria-label="술을 빨리 깨는 지름길은 없고 몸이 처리할 시간이 필요합니다">
        <div className="recovery-clock__ring"><span>몸의 시간</span><strong>시간이 필요해요</strong></div>
        <p><b>커피</b>도 <b>찬물 샤워</b>도 처리 속도를 당기지 못해요.</p>
      </div>
    </header>

    <aside className="recovery-alert" aria-labelledby="quick-danger-title">
      <span className="recovery-alert__mark" aria-hidden="true">!</span>
      <div><span className="eyebrow">숙취가 아니라 응급일 수 있어요</span><h2 id="quick-danger-title">깨우기 어렵거나 호흡이 이상하면 지금 119</h2><p>반복 구토·경련·차갑고 창백한 피부도 기다려 볼 증상이 아닙니다. 커피나 샤워로 깨우려 하지 마세요.</p></div>
      <a className="button recovery-alert__call" href="tel:119">119 전화</a>
    </aside>

    <section className="recovery-section-block" aria-labelledby="recover-facts-title">
      <div className="recovery-section-heading"><span className="eyebrow">먼저 기억할 세 가지</span><h2 id="recover-facts-title">‘해독 비법’보다 먼저 볼 사실</h2></div>
      <div className="recovery-principles">{recoveryPrinciples.map((card) => <article key={card.id}>
        <EvidenceBadge card={card} />
        <h3>{card.title}</h3>
        <strong>{card.summary}</strong>
        <p>{card.detail}</p>
        <SourceLinks ids={card.sourceIds} />
      </article>)}</div>
    </section>

    <section className="recovery-section-block" id="recover-now" aria-labelledby="recover-now-title">
      <div className="recovery-section-heading"><span className="eyebrow">상태별 첫 행동</span><h2 id="recover-now-title">지금부터 다음날까지</h2><p>개인별로 술이 깨는 시간을 계산하지 않습니다. 눈앞의 안전한 행동만 안내해요.</p></div>
      <ol className="recovery-timeline">{recoveryTimeline.map((phase, index) => <li key={phase.id}>
        <div className="recovery-timeline__number" aria-hidden="true">{index + 1}</div>
        <div><span>{phase.label}</span><h3>{phase.title}</h3><ul>{phase.actions.map((action) => <li key={action}>{action}</li>)}</ul></div>
      </li>)}</ol>
    </section>

    <section className="recovery-section-block" id="recover-food" aria-labelledby="recover-food-title">
      <div className="recovery-section-heading"><span className="eyebrow">무엇을 먹을까요?</span><h2 id="recover-food-title">속 상태로 고르는 회복 한 끼</h2><p>특정 음식에 해독·치료 효과를 붙이지 않습니다. 먹기 편한 평소 음식으로 수분과 에너지를 보충하는 선택지예요.</p></div>
      <RecoveryCards cards={recoveryFoods} kind="food" />
      <div className="recovery-place-link"><div><span aria-hidden="true">🥣</span><div><b>근처에서 먹을 곳도 함께 찾을까요?</b><p>맑은 국물·얼큰한 국물·죽·면처럼 메뉴 취향으로만 찾고, 치료 효능 순위는 만들지 않아요.</p></div></div><Link className="button button--ghost" href="/places">다음날 한 끼 찾기</Link></div>
    </section>

    <section className="recovery-section-block" id="recover-supplements" aria-labelledby="recover-supplements-title">
      <div className="recovery-section-heading"><span className="eyebrow">영양제·숙취해소 식품·약</span><h2 id="recover-supplements-title">광고 문구와 실제 근거를 나눠 봐요</h2><p>제품을 추천하거나 복용량을 처방하지 않습니다. 표시·광고의 실증 여부도 실제 알코올 제거 완료나 운전 가능을 뜻하지 않습니다.</p></div>
      <RecoveryCards cards={recoverySupplements} kind="supplement" />
      <aside className="medicine-note"><span aria-hidden="true">＋</span><div><b>약 이름보다 성분을 먼저 보세요.</b><p>두통약·감기약·수면제에는 술과 상호작용할 수 있는 성분이 겹쳐 있을 수 있습니다. 복용 중인 약이나 간·위장 질환이 있다면 약사·의료진에게 확인하세요.</p></div></aside>
    </section>

    <section className="recovery-section-block" aria-labelledby="recover-myth-title">
      <div className="recovery-section-heading"><span className="eyebrow">술자리 오해 바로잡기</span><h2 id="recover-myth-title">익숙한 말이 사실은 아니에요</h2></div>
      <div className="recovery-myths">{recoveryMyths.map((item) => <details key={item.myth}>
        <summary><span>오해</span>{item.myth}</summary>
        <div><b>사실</b><p>{item.fact}</p><SourceLinks ids={item.sourceIds} /></div>
      </details>)}</div>
    </section>

    <section className="recovery-emergency" id="recover-emergency" aria-labelledby="recover-emergency-title">
      <div className="recovery-emergency__head"><span className="eyebrow">응급 행동 카드</span><h2 id="recover-emergency-title">하나라도 의심되면 혼자 판단하지 마세요</h2><p>사람마다 위험해지는 양은 다릅니다. 아래 신호가 모두 나타날 때까지 기다리지 않습니다.</p></div>
      <div className="recovery-emergency__grid"><div><h3>위험 신호</h3><ul>{alcoholOverdoseSigns.map((sign) => <li key={sign}>{sign}</li>)}</ul></div><div><h3>도움을 기다리며</h3><ol>{alcoholOverdoseActions.map((action) => <li key={action}>{action}</li>)}</ol></div></div>
      <div className="recovery-emergency__actions"><a className="button" href="tel:119">119에 전화하기</a><a className="button button--ghost" href="https://www.e-gen.or.kr/egen/main.do" target="_blank" rel="noreferrer">E-GEN에서 응급실 찾기 ↗</a></div>
    </section>

    <section className="recovery-sources" aria-labelledby="recover-sources-title">
      <div><span className="eyebrow">출처와 한계</span><h2 id="recover-sources-title">확인한 공공기관·문헌</h2><p>일반 안전정보이며 개인의 진단·치료를 대신하지 않습니다. 질환·임신·복용 약·반복되는 과음이 있으면 의료진과 상의하세요.</p></div>
      <details><summary>출처 {recoverySources.length}개 모두 보기</summary><ol>{recoverySources.map((source) => <li key={source.id}><a href={source.url} target="_blank" rel="noreferrer"><b>{source.organization}</b> — {source.title} ↗</a><small>확인 {source.checkedAt}</small></li>)}</ol></details>
    </section>
  </article>;
}
